import React, { useEffect, useState, useRef } from 'react';
import './App.css';

const services = [
  {
    title: '5-daagse Spanje Retreat',
    description:
      'Reset in de zon met dagelijkse coaching, herstelroutines en natuurwandelingen voor een kalm zenuwstelsel.',
    duration: 'All-inclusive aan de Spaanse kust',
    icon: '\u2600',
    image: `${process.env.PUBLIC_URL}/vertical-shot-female-walking-narrow-streets-cadiz.jpg`
  },
  {
    title: 'Burn-out Herstel Coaching',
    description:
      'Online of live sessies rond energie, grenzen en een praktische structuur die past bij jouw leven.',
    duration: 'Traject van 6 of 12 weken',
    icon: '\u2665'
  },
  {
    title: 'Veerkracht voor Teams',
    description:
      'Workshops en 1-op-1 voor leiders die hun teams gezond, helder en gemotiveerd willen houden.',
    duration: 'Maatwerk programma',
    icon: '\u2605'
  }
];

const testimonials = [
  {
    name: 'Priya',
    location: 'Londen',
    quote:
      'Ik kwam uitgeput aan en vertrok uit Spanje met een duidelijke routine en de rust die ik miste. Nina luistert en doet.',
    result: 'Weer doorslapen zonder paniek'
  },
  {
    name: 'Carlos',
    location: 'Barcelona',
    quote:
      'De mix van coaching en dagelijkse check-ins gaf me houvast, ook na de retreat. Concreet en menselijk.',
    result: 'Met vertrouwen terug aan het werk'
  },
  {
    name: 'Sanne',
    location: 'Utrecht',
    quote:
      'In een maand tijd voel ik meer ruimte in mijn hoofd. De oefeningen zijn haalbaar naast mijn werk en gezin.',
    result: 'Grenzen stellen zonder schuldgevoel'
  },
  {
    name: 'Marcus',
    location: 'Toronto',
    quote:
      'De structuur en de check-ins gaven me precies genoeg sturing. Ik kon blijven reizen en toch herstellen.',
    result: 'Meer energie en focus op mijn startup'
  }
];

const steps = [
  { num: '01', title: 'Gratis intake', desc: 'We bespreken waar je staat en wat je nodig hebt in een vrijblijvend gesprek van 20 minuten.' },
  { num: '02', title: 'Persoonlijk plan', desc: 'Op basis van jouw situatie stel ik een traject samen dat past bij je energie en agenda.' },
  { num: '03', title: 'Coaching & reset', desc: 'Via retreat in Spanje of online sessies werken we samen aan jouw herstel met bewezen methodes.' },
  { num: '04', title: 'Blijvend resultaat', desc: 'Met tools, routines en follow-up sessies zorg je dat de verandering beklijft.' }
];

function useOnScreen(options) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.15, ...options });
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [options]);
  return [ref, isVisible];
}

function FadeIn({ children, className = '', delay = 0 }) {
  const [ref, isVisible] = useOnScreen();
  return (
    <div
      ref={ref}
      className={`fade-in ${isVisible ? 'visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const logoWhite = `${process.env.PUBLIC_URL}/LA-white.png`;
  const logoDark = `${process.env.PUBLIC_URL}/LA-dark.png`;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="page">
      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-inner">
          <a href="#start" className="brand" onClick={() => setMenuOpen(false)}>
            <img
              src={scrolled || menuOpen ? logoDark : logoWhite}
              alt="La Nina Coaching logo"
              className="brand-logo"
            />
          </a>

          <button
            className="menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span className={`hamburger ${menuOpen ? 'open' : ''}`} />
          </button>

          <div className={`nav-links ${menuOpen ? 'nav-open' : ''}`}>
            <a href="#approach" className="nav-link" onClick={() => setMenuOpen(false)}>Aanpak</a>
            <a href="#retreat" className="nav-link" onClick={() => setMenuOpen(false)}>Retreat</a>
            <a href="#process" className="nav-link" onClick={() => setMenuOpen(false)}>Werkwijze</a>
            <a href="#story" className="nav-link" onClick={() => setMenuOpen(false)}>Over Nina</a>
            <a href="#contact" className="btn btn-primary btn-sm" onClick={() => setMenuOpen(false)}>Plan een gesprek</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="hero" id="start">
        <div className="hero-bg">
          <img
            src={`${process.env.PUBLIC_URL}/vertical-shot-female-walking-narrow-streets-cadiz.jpg`}
            alt=""
            className="hero-bg-img"
          />
          <div className="hero-overlay" />
        </div>
        <div className="hero-content">
          <FadeIn>
            <span className="pill">Volgende retreat: mei & juni 2026</span>
          </FadeIn>
          <FadeIn delay={100}>
            <h1 className="hero-title">
              Krijg je leven terug.<br />
              <span className="hero-title-accent">5 dagen Spanje om herboren te voelen.</span>
            </h1>
          </FadeIn>
          <FadeIn delay={200}>
            <p className="hero-lead">
              Ik begeleid je van overprikkeling naar rust met een retreat in Spanje en
              coaching op afstand. Geen standaard schema, maar een plan dat bij jouw leven past.
            </p>
          </FadeIn>
          <FadeIn delay={300}>
            <div className="hero-actions">
              <a href="#retreat" className="btn btn-primary btn-lg">Boek de Spanje retreat</a>
              <a href="#contact" className="btn btn-outline-light btn-lg">Gratis intake</a>
            </div>
          </FadeIn>
          <FadeIn delay={400}>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="hero-stat-num">150+</span>
                <span className="hero-stat-label">Coaching sessies</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="hero-stat-num">5 dagen</span>
                <span className="hero-stat-label">Reset in Spanje</span>
              </div>
              <div className="hero-stat-divider" />
              <div className="hero-stat">
                <span className="hero-stat-num">12 weken</span>
                <span className="hero-stat-label">Blijvende rust</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </header>

      <main>
        {/* Trust bar */}
        <section className="trust-bar">
          <div className="container">
            <p className="trust-text">Gecertificeerd coach &middot; Nederlands & Engels &middot; Online & in-person</p>
          </div>
        </section>

        {/* Services / Approach */}
        <section className="section" id="approach">
          <div className="container">
            <FadeIn>
              <div className="section-header section-header-center">
                <span className="label">Wat ik bied</span>
                <h2>Menselijke coaching,<br /><em>persoonlijke aanpak</em></h2>
                <p className="section-sub">
                  In Spanje of online: we combineren kalme routines, zorg voor je zenuwstelsel en
                  heldere grenzen zodat jij weer leiding neemt over je leven.
                </p>
              </div>
            </FadeIn>
            <div className="services-grid">
              {services.map((service, i) => (
                <FadeIn key={service.title} delay={i * 120}>
                  <article className={`service-card ${service.image ? 'service-card-image' : ''}`}>
                    {service.image && (
                      <div className="service-card-bg">
                        <img src={service.image} alt="" />
                        <div className="service-card-overlay" />
                      </div>
                    )}
                    <div className="service-card-content">
                      <span className="service-icon">{service.icon}</span>
                      <span className="service-badge">{service.duration}</span>
                      <h3>{service.title}</h3>
                      <p>{service.description}</p>
                      <span className="service-link">Meer weten &rarr;</span>
                    </div>
                  </article>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="section section-warm" id="process">
          <div className="container">
            <FadeIn>
              <div className="section-header section-header-center">
                <span className="label">Werkwijze</span>
                <h2>Van eerste stap<br /><em>naar blijvend resultaat</em></h2>
              </div>
            </FadeIn>
            <div className="process-grid">
              {steps.map((step, i) => (
                <FadeIn key={step.num} delay={i * 100}>
                  <div className="process-step">
                    <span className="process-num">{step.num}</span>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="section" id="retreat">
          <div className="container">
            <FadeIn>
              <div className="section-header section-header-center">
                <span className="label">Retreat & trajecten</span>
                <h2>Kies hoe jij<br /><em>wilt resetten</em></h2>
                <p className="section-sub">
                  Spanje is voor een snelle reset. Hybride coaching houdt je op koers zodra je thuis
                  bent. We bouwen het programma rond jouw energie en agenda.
                </p>
              </div>
            </FadeIn>
            <div className="pricing-grid">
              <FadeIn delay={0}>
                <div className="pricing-card">
                  <div className="pricing-header">
                    <span className="pricing-name">Reset in Spanje</span>
                    <span className="pricing-price">&euro;2.950</span>
                    <span className="pricing-period">5 dagen all-inclusive</span>
                  </div>
                  <ul className="pricing-features">
                    <li>5 dagen coaching en herstelrituelen</li>
                    <li>Alle maaltijden en accommodatie</li>
                    <li>Natuurwandelingen en rustige avonden</li>
                    <li>4 online vervolg sessies</li>
                  </ul>
                  <a href="#contact" className="btn btn-outline btn-block">Boek retreat</a>
                </div>
              </FadeIn>
              <FadeIn delay={120}>
                <div className="pricing-card pricing-featured">
                  <div className="pricing-badge">Populair</div>
                  <div className="pricing-header">
                    <span className="pricing-name">Hybride Herstel</span>
                    <span className="pricing-price">&euro;1.140</span>
                    <span className="pricing-period">12 weken traject</span>
                  </div>
                  <ul className="pricing-features">
                    <li>Kick-off en 8 online sessies</li>
                    <li>Wekelijkse accountability en voice notes</li>
                    <li>Slaap-, focus- en grenzen toolkit</li>
                    <li>Planning in elke tijdzone</li>
                  </ul>
                  <a href="#contact" className="btn btn-primary btn-block">Start hybride traject</a>
                </div>
              </FadeIn>
              <FadeIn delay={240}>
                <div className="pricing-card">
                  <div className="pricing-header">
                    <span className="pricing-name">Team Veerkracht</span>
                    <span className="pricing-price">Maatwerk</span>
                    <span className="pricing-period">Op aanvraag</span>
                  </div>
                  <ul className="pricing-features">
                    <li>Workshops en 1-op-1 voor leiders</li>
                    <li>Burn-out preventie plan</li>
                    <li>Ontworpen voor remote teams</li>
                    <li>Doorlopende ondersteuning</li>
                  </ul>
                  <a href="#contact" className="btn btn-outline btn-block">Vraag offerte aan</a>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="section section-dark" id="testimonials">
          <div className="container">
            <FadeIn>
              <div className="section-header section-header-center">
                <span className="label label-light">Ervaringen</span>
                <h2 className="text-light">Wat klanten zeggen<br /><em>over de impact</em></h2>
              </div>
            </FadeIn>
            <TestimonialSlider />
          </div>
        </section>

        {/* About / Story */}
        <section className="section" id="story">
          <div className="container">
            <div className="about-grid">
              <FadeIn>
                <div className="about-image-wrap">
                  <img
                    src={`${process.env.PUBLIC_URL}/flamenco-illustration.jpg`}
                    alt="Nina - La Nina Coaching"
                    className="about-image"
                  />
                </div>
              </FadeIn>
              <FadeIn delay={150}>
                <div className="about-content">
                  <span className="label">Mijn verhaal</span>
                  <h2>Hoi, ik ben <em>Nina</em></h2>
                  <p>
                    Ik startte La Nina Venture nadat ik zelf een burn-out
                    kreeg door een auto-ongeluk. Een retreat in Spanje bracht me terug bij mezelf.
                  </p>
                  <p>
                    Met die ervaring begeleid ik nu anderen met structuur, warmte en bewezen tools.
                    Coaching in het Nederlands en Engels, online of in-person.
                  </p>
                  <div className="about-values">
                    <div className="about-value">
                      <span className="about-value-icon">&#10047;</span>
                      <div>
                        <strong>Evidence-based</strong>
                        <span>Methodes die werken</span>
                      </div>
                    </div>
                    <div className="about-value">
                      <span className="about-value-icon">&#9829;</span>
                      <div>
                        <strong>Persoonlijk</strong>
                        <span>Geen standaard aanpak</span>
                      </div>
                    </div>
                    <div className="about-value">
                      <span className="about-value-icon">&#9992;</span>
                      <div>
                        <strong>Flexibel</strong>
                        <span>NL & EN, online of live</span>
                      </div>
                    </div>
                  </div>
                  <a href="#contact" className="btn btn-primary">Plan een gesprek met mij</a>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cta-section" id="contact">
          <div className="container">
            <FadeIn>
              <div className="cta-content">
                <span className="label label-light">Klaar voor rust?</span>
                <h2 className="text-light">Plan een gratis intake<br />van 20 minuten</h2>
                <p className="cta-sub">
                  Vertel waar je nu staat en ga weg met twee acties om direct te starten.
                </p>
                <div className="cta-actions">
                  <a href="mailto:info@laninacoaching.com" className="btn btn-white btn-lg">Boek je intake</a>
                  <a href="mailto:info@laninacoaching.com" className="btn btn-outline-light btn-lg">Stel een vraag</a>
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-brand">
                <img
                  src={logoWhite}
                  alt="La Nina Coaching"
                  className="footer-logo"
                />
                <p className="footer-tagline">Burn-out & stress coaching met hart en structuur.</p>
              </div>
              <div className="footer-col">
                <h4>Navigatie</h4>
                <a href="#approach">Aanpak</a>
                <a href="#retreat">Retreat & Prijzen</a>
                <a href="#process">Werkwijze</a>
                <a href="#story">Over Nina</a>
              </div>
              <div className="footer-col">
                <h4>Aanbod</h4>
                <a href="#retreat">Spanje Retreat</a>
                <a href="#retreat">Hybride Coaching</a>
                <a href="#retreat">Team Veerkracht</a>
              </div>
              <div className="footer-col">
                <h4>Contact</h4>
                <a href="mailto:info@laninacoaching.com">info@laninacoaching.com</a>
                <a href="#contact">Plan een gesprek</a>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 La Nina Coaching. Alle rechten voorbehouden.</p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default App;

function TestimonialSlider() {
  const [index, setIndex] = useState(0);
  const slideCount = testimonials.length;

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % slideCount);
    }, 5000);
    return () => clearInterval(id);
  }, [slideCount]);

  const prev = () => setIndex((p) => (p - 1 + slideCount) % slideCount);
  const next = () => setIndex((p) => (p + 1) % slideCount);

  return (
    <div className="testimonial-slider">
      <div className="testimonial-window">
        <div
          className="testimonial-track"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {testimonials.map((item) => (
            <article className="testimonial-card" key={item.name}>
              <div className="testimonial-quote-mark">&ldquo;</div>
              <p className="testimonial-quote">{item.quote}</p>
              <div className="testimonial-meta">
                <div className="testimonial-avatar">{item.name[0]}</div>
                <div>
                  <span className="testimonial-name">{item.name}</span>
                  <span className="testimonial-location">{item.location}</span>
                </div>
              </div>
              <div className="testimonial-result">{item.result}</div>
            </article>
          ))}
        </div>
      </div>
      <div className="testimonial-controls">
        <button className="slider-btn" onClick={prev} aria-label="Vorige">&larr;</button>
        <div className="dots">
          {testimonials.map((_, i) => (
            <button
              key={i}
              className={`dot ${i === index ? 'active' : ''}`}
              onClick={() => setIndex(i)}
              aria-label={`Testimonial ${i + 1}`}
            />
          ))}
        </div>
        <button className="slider-btn" onClick={next} aria-label="Volgende">&rarr;</button>
      </div>
    </div>
  );
}
